import { useEffect, useState } from "react";
import { floorPlans } from "../../lib/api";
import { toast } from "sonner";
import { Trash2, Save, Plus } from "lucide-react";
import FloorPlanMap from "../../components/FloorPlanMap";

export default function AdminFloorPlans() {
  const [plans, setPlans] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [draft, setDraft] = useState(null);

  const load = () => floorPlans.list().then(d => { setPlans(d); if (!activeId && d.length) { setActiveId(d[0].id); setDraft(JSON.parse(JSON.stringify(d[0]))); } });
  useEffect(() => { load(); }, []); // eslint-disable-line

  const switchPlan = (id) => {
    const p = plans.find(x => x.id === id);
    setActiveId(id); setDraft(JSON.parse(JSON.stringify(p)));
  };

  const updateTable = (idx, key, value) => {
    const next = JSON.parse(JSON.stringify(draft));
    next.tables[idx][key] = key === "min_spend" || key === "capacity" || key === "x" || key === "y" ? parseFloat(value) || 0 : value;
    setDraft(next);
  };

  const addTable = () => {
    const next = JSON.parse(JSON.stringify(draft));
    const existing = next.tables.map(t => t.label);
    let i = next.tables.length + 1;
    let label = `T${i}`;
    while (existing.includes(label)) { i++; label = `T${i}`; }
    next.tables.push({
      id: `new-${Date.now()}-${i}`,
      label,
      section: next.tables[0]?.section || "main_rooftop",
      capacity: 6,
      min_spend: 500,
      category: "lounge",
      status: "available",
      x: 50,
      y: 50,
      bottle_packages: [],
    });
    setDraft(next);
    toast.success(`Added ${label}`);
  };

  const removeTable = (idx) => {
    if (!window.confirm(`Remove table ${draft.tables[idx].label}?`)) return;
    const next = JSON.parse(JSON.stringify(draft));
    next.tables.splice(idx, 1);
    setDraft(next);
  };

  const save = async () => {
    try {
      await floorPlans.update(draft.id, draft);
      toast.success("Floor plan saved");
      load();
    } catch (e) { toast.error("Save failed"); }
  };

  if (!draft) return <p className="text-white/40 italic font-display">Loading…</p>;

  return (
    <div data-testid="admin-floorplans">
      <div className="flex justify-between items-center mb-6">
        <div>
          <span className="eyebrow">— Floor Plans</span>
          <h1 className="font-display text-4xl mt-2">Tables & <span className="italic text-gold">Layout</span></h1>
        </div>
        <div className="flex gap-2">
          <button onClick={addTable} className="btn-luxe btn-luxe-ghost" data-testid="add-table"><Plus size={14} /> Add Table</button>
          <button onClick={save} className="btn-luxe btn-luxe-solid" data-testid="save-floorplan"><Save size={14} /> Save</button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        {plans.map(p => (
          <button key={p.id} onClick={() => switchPlan(p.id)} className={`px-4 py-2 text-[11px] tracking-luxe uppercase border ${activeId === p.id ? "border-gold text-gold" : "border-white/15 text-white/60"}`}>
            {p.name}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <p className="eyebrow mb-3">Preview</p>
          <FloorPlanMap plan={draft} onSelect={() => {}} selectedTableId={null} />
        </div>
        <div>
          <p className="eyebrow mb-3">Table Inventory ({draft.tables.length})</p>
          <div className="border border-white/5 overflow-x-auto max-h-[600px] overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-[var(--onyx-charcoal)] text-[10px] uppercase tracking-luxe-tight text-white/50 sticky top-0">
                <tr><th className="p-2 text-left">Label</th><th>Section</th><th>Cap</th><th>Min $</th><th>Status</th><th>Cat</th><th></th></tr>
              </thead>
              <tbody>
                {draft.tables.map((t, i) => (
                  <tr key={t.id} className="border-t border-white/5">
                    <td className="p-2"><input className="bg-transparent border border-white/10 w-16 px-1 py-0.5 font-display text-base" value={t.label} onChange={e => updateTable(i, "label", e.target.value)} /></td>
                    <td><input className="bg-transparent border border-white/10 w-24 px-1 py-0.5" value={t.section} onChange={e => updateTable(i, "section", e.target.value)} /></td>
                    <td><input type="number" className="bg-transparent border border-white/10 w-14 px-1 py-0.5" value={t.capacity} onChange={e => updateTable(i, "capacity", e.target.value)} /></td>
                    <td><input type="number" className="bg-transparent border border-white/10 w-20 px-1 py-0.5 text-gold" value={t.min_spend} onChange={e => updateTable(i, "min_spend", e.target.value)} /></td>
                    <td>
                      <select className="bg-transparent border border-white/10 px-1 py-0.5" value={t.status} onChange={e => updateTable(i, "status", e.target.value)}>
                        <option value="available">available</option>
                        <option value="limited">limited</option>
                        <option value="reserved">reserved</option>
                        <option value="unavailable">unavailable</option>
                      </select>
                    </td>
                    <td>
                      <select className="bg-transparent border border-white/10 px-1 py-0.5" value={t.category} onChange={e => updateTable(i, "category", e.target.value)}>
                        <option value="vip">vip</option>
                        <option value="premium">premium</option>
                        <option value="lounge">lounge</option>
                        <option value="standard">standard</option>
                      </select>
                    </td>
                    <td className="px-2">
                      <button onClick={() => removeTable(i)} className="text-red-400 hover:text-red-300" data-testid={`remove-table-${t.label}`} title="Remove table"><Trash2 size={14} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[11px] text-white/40 mt-2">Click <span className="text-white/70">Save</span> after edits. Changes go live for guests immediately.</p>
        </div>
      </div>
    </div>
  );
}
